import { useState, useEffect, useCallback, CSSProperties } from "react";

// ── Brand tokens ────────────────────────────────────────────────
const BG_ROOT   = "#0d0d0d";
const BG_CARD   = "rgba(255,255,255,0.025)";
const MINT      = "#50fa7b";
const MINT_DIM  = "rgba(80,250,123,0.12)";
const MINT_GLOW = "0 0 18px rgba(80,250,123,0.25), 0 0 4px rgba(80,250,123,0.40)";
const RED       = "#ff5555";
const RED_DIM   = "rgba(255,120,100,0.12)";
const BORDER    = "rgba(255,255,255,0.07)";
const GLASS_BORDER = "1px solid rgba(255,255,255,0.10)";
const GLASS_BG  = "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)";
const TEXT       = "#f8f8f2";
const TEXT_DIM   = "#6272a4";
const MONO       = "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace";
const SANS       = "'JetBrains Mono', 'Fira Code', monospace";

const TRANSITION_PANEL  = "opacity 0.28s ease, transform 0.28s ease";
const TRANSITION_FAST   = "all 0.18s ease";

// ── Shared micro-components ─────────────────────────────────────

/** Pulsing status dot */
function StatusDot({ alive, size = 8 }: { alive: boolean; size?: number }) {
  return (
    <span style={{
      display: "inline-block",
      width: size, height: size, borderRadius: "50%",
      background: alive ? MINT : RED,
      boxShadow: alive
        ? `0 0 6px ${MINT}, 0 0 12px rgba(80,250,123,0.3)`
        : `0 0 6px ${RED}`,
      animation: alive ? "settingsPulse 2.2s ease-in-out infinite" : "none",
      flexShrink: 0,
    }} />
  );
}

/** Glassmorphism card wrapper */
function GlassCard({ children, active, onClick, style }: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
}) {
  const base: CSSProperties = {
    position: "relative",
    padding: "0.9rem 1.1rem",
    borderRadius: "10px",
    border: active ? `1px solid rgba(80,250,123,0.40)` : GLASS_BORDER,
    background: active ? "rgba(80,250,123,0.06)" : GLASS_BG,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    boxShadow: active ? MINT_GLOW : "0 1px 3px rgba(0,0,0,0.3)",
    cursor: onClick ? "pointer" : "default",
    transition: TRANSITION_FAST,
    ...style,
  };
  return (
    <div onClick={onClick} style={base} onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
    }} onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
    }}>
      {children}
    </div>
  );
}

// ── Types ───────────────────────────────────────────────────────

interface ProviderEntry {
  id: string;
  display_name: string;
  active: boolean;
}

interface OllamaModel {
  name: string;
  size_display: string;
  parameter_size: string;
  quantization: string;
}

interface OllamaStatus {
  running: boolean;
  url: string;
  active_model: string;
  model_count: number;
  models: OllamaModel[];
  error: string | null;
  setup_instructions?: {
    install: string;
    start: string;
    pull_model: string;
    verify: string;
  };
}

const SUGGESTED_MODELS = [
  { name: "llama3.1", desc: "Meta Llama 3.1 8B — strong all-rounder", size: "~4.7 GB" },
  { name: "mistral", desc: "Mistral 7B — fast, efficient", size: "~4.1 GB" },
  { name: "codellama", desc: "Code Llama 7B — code-specialized", size: "~3.8 GB" },
  { name: "deepseek-coder-v2", desc: "DeepSeek Coder V2 — top-tier code model", size: "~8.9 GB" },
  { name: "phi3", desc: "Microsoft Phi-3 Mini — compact and capable", size: "~2.3 GB" },
  { name: "gemma2", desc: "Google Gemma 2 — balanced performance", size: "~5.4 GB" },
  { name: "qwen2.5-coder", desc: "Qwen 2.5 Coder — excellent code generation", size: "~4.7 GB" },
  { name: "starcoder2", desc: "StarCoder2 3B — lightweight code model", size: "~1.7 GB" },
];

const PROVIDER_META: Record<string, string> = {
  ollama:      "Local inference — no API key, no cloud",
  anthropic:   "Anthropic Claude API (requires ANTHROPIC_API_KEY)",
  openai:      "OpenAI API (requires OPENAI_API_KEY)",
  codex:       "OpenAI Codex CLI (local binary)",
  "claude-cli":"Claude Code CLI (OAuth)",
};

type Tab = "providers" | "ollama" | "about";

// ── Keyframe injection (runs once) ──────────────────────────────
const KEYFRAMES_ID = "__settings-wb-kf";
if (typeof document !== "undefined" && !document.getElementById(KEYFRAMES_ID)) {
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
@keyframes settingsPulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
}
@keyframes settingsFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes settingsDiamondSpin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
  `;
  document.head.appendChild(style);
}

// ── Component ───────────────────────────────────────────────────

export function SettingsWorkbench() {
  const [providers, setProviders] = useState<ProviderEntry[]>([]);
  const [activeProvider, setActiveProvider] = useState("");
  const [ollamaStatus, setOllamaStatus] = useState<OllamaStatus | null>(null);
  const [pulling, setPulling] = useState<string | null>(null);
  const [pullProgress, setPullProgress] = useState("");
  const [tab, setTab] = useState<Tab>("providers");

  const loadProviders = useCallback(async () => {
    try {
      const res = await fetch("/api/providers");
      const data = await res.json();
      setProviders(data.providers || []);
      setActiveProvider(data.active || "");
    } catch { /* ignore */ }
  }, []);

  const loadOllamaStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/ollama/status");
      const data = await res.json();
      setOllamaStatus(data);
    } catch {
      setOllamaStatus({
        running: false,
        url: "http://localhost:11434",
        active_model: "llama3.1",
        model_count: 0,
        models: [],
        error: "Could not reach OpenTool API",
      });
    }
  }, []);

  useEffect(() => {
    loadProviders();
    loadOllamaStatus();
  }, [loadProviders, loadOllamaStatus]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.type === "ollama-pull-progress") {
        setPullProgress(detail.progress || "");
        if (detail.progress === "done") {
          setPulling(null);
          setPullProgress("");
          loadOllamaStatus();
        }
      }
    };
    window.addEventListener("opentool:ollama-pull-progress", handler);
    return () => window.removeEventListener("opentool:ollama-pull-progress", handler);
  }, [loadOllamaStatus]);

  const switchProvider = async (id: string) => {
    setActiveProvider(id);
    try {
      const res = await fetch("/api/provider", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.active) setActiveProvider(data.active);
    } catch { /* keep optimistic update */ }
  };

  const pullModel = async (model: string) => {
    setPulling(model);
    setPullProgress("starting...");
    await fetch("/api/ollama/pull", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model }),
    });
  };

  const isInstalled = (name: string) =>
    ollamaStatus?.models.some((m) => m.name.startsWith(name)) ?? false;

  // ── Render ──────────────────────────────────────────────────

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: BG_ROOT, color: TEXT, fontFamily: MONO,
    }}>

      {/* ──── Header ──── */}
      <div style={{
        padding: "1.25rem 1.5rem",
        borderBottom: `1px solid ${BORDER}`,
        display: "flex", alignItems: "center", gap: "1rem",
      }}>
        <h2 style={{
          fontSize: "1.1rem", fontWeight: 700, margin: 0,
          fontFamily: SANS, letterSpacing: "-0.01em",
        }}>Settings</h2>

        {/* Tab bar */}
        <div style={{
          display: "flex", gap: "4px", marginLeft: "auto",
          background: "rgba(255,255,255,0.03)", borderRadius: "8px", padding: "3px",
        }}>
          {(["providers", "ollama", "about"] as const).map((t) => {
            const isActive = tab === t;
            return (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: "0.35rem 0.85rem", borderRadius: "6px", border: "none",
                cursor: "pointer", fontSize: "0.75rem", fontFamily: MONO,
                textTransform: "capitalize", transition: TRANSITION_FAST,
                background: isActive ? MINT_DIM : "transparent",
                color: isActive ? MINT : TEXT_DIM,
                fontWeight: isActive ? 600 : 400,
              }}>{t}</button>
            );
          })}
        </div>
      </div>

      {/* ──── Panel body ──── */}
      <div style={{
        flex: 1, overflow: "auto", padding: "1.5rem",
      }}>

        {/* ══════ Providers Tab ══════ */}
        {tab === "providers" && (
          <div key="providers-panel" style={{ animation: "settingsFadeIn 0.3s ease both" }}>
            <p style={{ color: TEXT_DIM, fontSize: "0.8rem", marginBottom: "1.25rem", lineHeight: 1.55, fontFamily: SANS }}>
              Select inference provider. Local-first: Ollama runs on your machine with zero cloud dependency.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {providers.map((p) => {
                const isActive = activeProvider === p.id;
                return (
                  <GlassCard key={p.id} active={isActive} onClick={() => switchProvider(p.id)} style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    {/* Indicator dot with glow */}
                    <span style={{
                      width: 11, height: 11, borderRadius: "50%", flexShrink: 0,
                      background: isActive ? MINT : "#282a36",
                      border: isActive ? "none" : "1px solid #282a36",
                      boxShadow: isActive ? MINT_GLOW : "none",
                      transition: TRANSITION_FAST,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.85rem", fontFamily: SANS }}>{p.display_name}</div>
                      <div style={{ fontSize: "0.7rem", color: TEXT_DIM, marginTop: "2px" }}>
                        {PROVIDER_META[p.id] || `Provider: ${p.id}`}
                      </div>
                    </div>
                    {p.id === "ollama" && (
                      <span style={{
                        display: "flex", alignItems: "center", gap: "5px",
                        fontSize: "0.65rem", padding: "0.2rem 0.55rem",
                        borderRadius: "20px",
                        background: ollamaStatus?.running ? MINT_DIM : RED_DIM,
                        color: ollamaStatus?.running ? MINT : RED,
                      }}>
                        <StatusDot alive={!!ollamaStatus?.running} size={6} />
                        {ollamaStatus?.running ? "running" : "offline"}
                      </span>
                    )}
                    {isActive && (
                      <span style={{
                        fontSize: "0.6rem", padding: "0.15rem 0.45rem",
                        borderRadius: "4px", background: MINT_DIM, color: MINT,
                        fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase",
                      }}>active</span>
                    )}
                  </GlassCard>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════ Ollama Tab ══════ */}
        {tab === "ollama" && (
          <div key="ollama-panel" style={{ animation: "settingsFadeIn 0.3s ease both" }}>

            {/* Status Banner */}
            <GlassCard style={{
              marginBottom: "1.5rem",
              borderColor: ollamaStatus?.running
                ? "rgba(80,250,123,0.25)"
                : "rgba(255,120,100,0.25)",
              boxShadow: ollamaStatus?.running
                ? "0 0 20px rgba(80,250,123,0.08)"
                : "0 0 20px rgba(255,120,100,0.08)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <StatusDot alive={!!ollamaStatus?.running} size={10} />
                <strong style={{ fontSize: "0.85rem", fontFamily: SANS }}>
                  {ollamaStatus?.running
                    ? `Ollama running at ${ollamaStatus.url}`
                    : "Ollama not running"}
                </strong>
                <button onClick={loadOllamaStatus} style={{
                  marginLeft: "auto", padding: "0.3rem 0.65rem", borderRadius: "6px",
                  border: GLASS_BORDER, background: "rgba(255,255,255,0.04)",
                  color: TEXT_DIM, cursor: "pointer", fontSize: "0.7rem", fontFamily: MONO,
                  transition: TRANSITION_FAST,
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.color = TEXT;
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                  e.currentTarget.style.color = TEXT_DIM;
                }}>refresh</button>
              </div>
              {ollamaStatus?.error && !ollamaStatus.running && (
                <div style={{ marginTop: "0.85rem", fontSize: "0.75rem", color: TEXT_DIM }}>
                  <p style={{ marginBottom: "0.5rem" }}>{ollamaStatus.error}</p>
                  {ollamaStatus.setup_instructions && (
                    <pre style={{
                      padding: "0.85rem", borderRadius: "8px",
                      background: "rgba(0,0,0,0.5)", fontSize: "0.7rem",
                      border: `1px solid ${BORDER}`,
                      whiteSpace: "pre-wrap", margin: 0, color: MINT,
                    }}>
{`# Install Ollama
${ollamaStatus.setup_instructions.install}

# Start the server
${ollamaStatus.setup_instructions.start}

# Pull a model
${ollamaStatus.setup_instructions.pull_model}`}
                    </pre>
                  )}
                </div>
              )}
            </GlassCard>

            {/* Installed Models */}
            {ollamaStatus?.running && (
              <>
                <h3 style={{
                  fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.85rem",
                  fontFamily: SANS, display: "flex", alignItems: "center", gap: "0.5rem",
                }}>
                  Installed Models
                  <span style={{
                    fontSize: "0.65rem", padding: "0.1rem 0.45rem", borderRadius: "20px",
                    background: MINT_DIM, color: MINT, fontFamily: MONO, fontWeight: 500,
                  }}>{ollamaStatus.model_count}</span>
                </h3>
                {ollamaStatus.models.length > 0 ? (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: "0.6rem", marginBottom: "1.75rem",
                  }}>
                    {ollamaStatus.models.map((m) => (
                      <GlassCard key={m.name} style={{ padding: "0.75rem 0.95rem" }}>
                        <div style={{ fontWeight: 600, fontSize: "0.82rem", marginBottom: "4px" }}>{m.name}</div>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          {m.parameter_size && (
                            <span style={{
                              fontSize: "0.6rem", padding: "0.1rem 0.35rem",
                              borderRadius: "4px", background: "rgba(80,250,123,0.08)",
                              color: MINT, border: "1px solid rgba(80,250,123,0.15)",
                            }}>{m.parameter_size}</span>
                          )}
                          {m.quantization && (
                            <span style={{
                              fontSize: "0.6rem", padding: "0.1rem 0.35rem",
                              borderRadius: "4px", background: "rgba(189,147,249,0.08)",
                              color: "#bd93f9", border: "1px solid rgba(189,147,249,0.15)",
                            }}>{m.quantization}</span>
                          )}
                          <span style={{
                            fontSize: "0.6rem", padding: "0.1rem 0.35rem",
                            borderRadius: "4px", background: "rgba(255,255,255,0.04)",
                            color: TEXT_DIM, marginLeft: "auto",
                          }}>{m.size_display}</span>
                        </div>
                      </GlassCard>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: TEXT_DIM, fontSize: "0.8rem", marginBottom: "1.75rem", fontFamily: SANS }}>
                    No models installed. Pull one below to get started.
                  </p>
                )}
              </>
            )}

            {/* Recommended Models Catalog */}
            <h3 style={{
              fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.85rem", fontFamily: SANS,
            }}>
              Recommended Models
            </h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "0.6rem",
            }}>
              {SUGGESTED_MODELS.map((m) => {
                const installed = isInstalled(m.name);
                const isPulling = pulling === m.name;
                return (
                  <GlassCard key={m.name} style={{
                    display: "flex", flexDirection: "column", gap: "0.5rem",
                    padding: "0.85rem 1rem",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      <span style={{
                        fontWeight: 700, fontSize: "0.82rem", fontFamily: MONO,
                      }}>{m.name}</span>
                      <span style={{
                        marginLeft: "auto", fontSize: "0.6rem", color: TEXT_DIM,
                        padding: "0.1rem 0.35rem", borderRadius: "4px",
                        background: "rgba(255,255,255,0.04)",
                      }}>{m.size}</span>
                    </div>
                    <div style={{ fontSize: "0.68rem", color: TEXT_DIM, lineHeight: 1.4, fontFamily: SANS }}>
                      {m.desc}
                    </div>
                    <div style={{ marginTop: "auto", display: "flex", justifyContent: "flex-end" }}>
                      {isPulling ? (
                        <span style={{
                          fontSize: "0.65rem", color: "#bd93f9",
                          display: "flex", alignItems: "center", gap: "5px",
                        }}>
                          <span style={{ animation: "settingsPulse 1.2s ease-in-out infinite" }}>
                            {pullProgress}
                          </span>
                        </span>
                      ) : installed ? (
                        <span style={{
                          fontSize: "0.65rem", color: MINT, fontWeight: 600,
                          padding: "0.2rem 0.5rem", borderRadius: "20px",
                          background: MINT_DIM,
                          display: "flex", alignItems: "center", gap: "4px",
                        }}>
                          <StatusDot alive size={5} /> installed
                        </span>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); pullModel(m.name); }}
                          disabled={!ollamaStatus?.running || !!pulling}
                          style={{
                            padding: "0.3rem 0.75rem", borderRadius: "6px", cursor: "pointer",
                            border: `1px solid rgba(80,250,123,0.30)`,
                            background: "rgba(80,250,123,0.08)",
                            color: MINT, fontSize: "0.7rem", fontFamily: MONO, fontWeight: 500,
                            opacity: (!ollamaStatus?.running || !!pulling) ? 0.35 : 1,
                            transition: TRANSITION_FAST,
                          }}>pull</button>
                      )}
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════ About Tab ══════ */}
        {tab === "about" && (
          <div key="about-panel" style={{
            animation: "settingsFadeIn 0.3s ease both",
            display: "flex", flexDirection: "column", alignItems: "center",
            paddingTop: "2rem",
          }}>
            {/* Diamond logo */}
            <div style={{
              width: 72, height: 72, position: "relative", marginBottom: "1.25rem",
            }}>
              {/* Outer glow ring */}
              <div style={{
                position: "absolute", inset: -8,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(80,250,123,0.12) 0%, transparent 70%)",
              }} />
              {/* Diamond shape via rotated square */}
              <div style={{
                width: "100%", height: "100%",
                display: "flex", alignItems: "center", justifyContent: "center",
                transform: "rotate(45deg)",
                borderRadius: "14px",
                background: "linear-gradient(135deg, rgba(80,250,123,0.18) 0%, rgba(80,250,123,0.04) 100%)",
                border: "1.5px solid rgba(80,250,123,0.35)",
                boxShadow: MINT_GLOW,
              }}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{ transform: "rotate(-45deg)" }}>
                  <path d="M14 2L26 14L14 26L2 14L14 2Z" stroke={MINT} strokeWidth="1.8" fill="none" />
                  <path d="M14 7L21 14L14 21L7 14L14 7Z" fill={MINT} fillOpacity="0.25" stroke={MINT} strokeWidth="1" />
                  <circle cx="14" cy="14" r="2.5" fill={MINT} />
                </svg>
              </div>
            </div>

            <h3 style={{
              fontSize: "1.3rem", fontWeight: 700, marginBottom: "0.3rem",
              fontFamily: SANS, letterSpacing: "-0.02em",
              background: `linear-gradient(135deg, ${TEXT} 0%, ${MINT} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              OpenTool
            </h3>
            <p style={{
              color: TEXT_DIM, fontSize: "0.78rem", marginBottom: "2rem",
              textAlign: "center", maxWidth: 360, lineHeight: 1.55, fontFamily: SANS,
            }}>
              Local-first coding workbench. Run entirely on your machine with Ollama,
              or connect to cloud APIs when you need them.
            </p>

            {/* Info cards */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: "0.6rem", width: "100%", maxWidth: 480,
            }}>
              {([
                ["Version",    "1.1.0"],
                ["License",    "MIT"],
                ["Platforms",  "Linux, macOS, Windows, WSL, Docker"],
                ["Default",    "Ollama (local)"],
                ["Fallbacks",  "Claude API, OpenAI API, CLI agents"],
                ["Runtime",    "Edge-native, air-gapped ready"],
              ] as const).map(([label, value]) => (
                <GlassCard key={label} style={{
                  padding: "0.65rem 0.85rem",
                  ...(label === "Platforms" ? { gridColumn: "1 / -1" } : {}),
                }}>
                  <div style={{ fontSize: "0.6rem", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "3px", fontFamily: SANS }}>
                    {label}
                  </div>
                  <div style={{ fontSize: "0.8rem", fontWeight: 600, fontFamily: label === "Version" ? MONO : SANS }}>
                    {value}
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Footer */}
            <div style={{
              marginTop: "2.5rem", fontSize: "0.6rem", color: "rgba(131,146,157,0.5)",
              fontFamily: MONO, letterSpacing: "0.04em",
            }}>
              opentool.io
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
